import { useCallback, useEffect, useState } from 'react';
import { Loader2, Save, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import { useChatModelOptions } from '@/hooks/useChatModelOptions';

const WORKFLOW_BUILDER_SERVICE_KEY = 'workflow_builder';

type ProviderUi = 'openai' | 'claude' | 'gemini';
const KNOWN_UI_PROVIDERS: readonly ProviderUi[] = ['openai', 'claude', 'gemini'];

// Provider live da `/ai-tools/available-providers?service_key=workflow_builder`
// (stesso pattern di RAGPlusSection). Il backend espone l'id canonical
// ('openai'|'claude'|'google'); la UI usa 'gemini' per 'google'.
interface AvailableProviderEntry {
  id: string;
  label: string;
  kind: 'canonical' | 'custom';
  capability_supported: boolean;
  is_valid: boolean;
  disabled_reason: string | null;
  reason_code?: string | null;
  health_status?: string;
}

interface ProviderOption {
  value: ProviderUi;
  label: string;
}

function providerToDb(p: string): string {
  return p === 'gemini' ? 'google' : p;
}
function backendIdToUi(id: string): ProviderUi | null {
  const v = id === 'google' ? 'gemini' : id;
  return (KNOWN_UI_PROVIDERS as readonly string[]).includes(v) ? (v as ProviderUi) : null;
}
function providerFromDb(p: string): ProviderUi {
  return backendIdToUi(p) ?? 'openai';
}
function stripProxySuffix(label: string): string {
  return label.replace(/\s*\(proxy\)\s*$/i, '').trim();
}

interface WorkflowBuilderConfigDto {
  model: string;
  provider: string;
  temperature: number;
  maxTokens: number;
}

export default function WorkflowBuilderAIConfigCard() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [providerUI, setProviderUI] = useState<ProviderUi>('openai');
  const [modelName, setModelName] = useState<string>('');
  const [temperature, setTemperature] = useState<number>(0.7);
  const [maxTokens, setMaxTokens] = useState<number>(2000);
  const [currentBadge, setCurrentBadge] = useState<{ model: string; provider: string } | null>(null);
  const [providerOptions, setProviderOptions] = useState<ProviderOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get<{ providers: AvailableProviderEntry[] }>(
          `/ai-tools/available-providers?service_key=${WORKFLOW_BUILDER_SERVICE_KEY}`
        );
        const list = res.success && res.data ? res.data.providers ?? [] : [];
        const mapped = list
          .filter((e) => e.kind === 'canonical' && e.capability_supported)
          .reduce<ProviderOption[]>((acc, e) => {
            const value = backendIdToUi(e.id);
            if (value && !acc.some((o) => o.value === value)) {
              acc.push({ value, label: stripProxySuffix(e.label) });
            }
            return acc;
          }, []);
        if (!cancelled) setProviderOptions(mapped);
      } catch (err) {
        console.error('[WorkflowBuilderAIConfigCard] providers load error:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Task #1402 — catalog v2 dinamico (sostituisce GET /ai-tools/llm-models/:provider).
  // Il `provider` di UI mappa 1:1 sul tipo `LLMProvider` (openai|claude|gemini).
  const modelsQuery = useChatModelOptions(providerUI);
  const availableModels = modelsQuery.data?.models ?? [];
  const modelsLoading = modelsQuery.isLoading;

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<WorkflowBuilderConfigDto>('/ai/workflow-builder/config');
      if (res.success && res.data) {
        const ui = providerFromDb(res.data.provider || 'openai');
        setProviderUI(ui);
        setModelName(res.data.model || '');
        setTemperature(typeof res.data.temperature === 'number' ? res.data.temperature : 0.7);
        setMaxTokens(typeof res.data.maxTokens === 'number' ? res.data.maxTokens : 2000);
        setCurrentBadge({ model: res.data.model, provider: ui });
      }
    } catch (err) {
      console.error('[WorkflowBuilderAIConfigCard] load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleProviderChange = (uiProvider: string) => {
    const next = providerFromDb(uiProvider);
    setProviderUI(next);
    setModelName('');
  };

  const save = async () => {
    if (!modelName.trim()) {
      toast({ title: 'Modello richiesto', description: 'Seleziona un modello', variant: 'destructive' });
      return;
    }
    if (temperature < 0 || temperature > 1.5) {
      toast({ title: 'Temperature fuori range', description: 'Valori ammessi: 0 – 1.5', variant: 'destructive' });
      return;
    }
    if (!Number.isFinite(maxTokens) || maxTokens <= 0 || maxTokens > 32000) {
      toast({ title: 'Max tokens fuori range', description: 'Valori ammessi: 1 – 32000', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await apiClient.put('/ai/workflow-builder/config', {
        provider: providerToDb(providerUI),
        model: modelName.trim(),
        temperature: Number(temperature),
        maxTokens: Math.floor(maxTokens),
      });
      setCurrentBadge({ model: modelName.trim(), provider: providerUI });
      toast({
        title: 'Configurazione salvata',
        description: `${providerUI} · ${modelName} · temp ${temperature} · max ${maxTokens}`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Impossibile salvare la configurazione';
      toast({ title: 'Errore salvataggio', description: message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-border/50">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <CardTitle className="text-base">Workflow Builder AI</CardTitle>
              <CardDescription className="text-xs">
                Provider, modello e parametri usati dal Workflow Builder Agent.
                Salvati in <code className="text-[10px]">platform_model_config.extra_config</code> · service_key=<code>workflow_builder</code>.
              </CardDescription>
            </div>
          </div>
          {currentBadge && (
            <Badge variant="outline" className="shrink-0 border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 text-[11px]">
              <span className="font-mono mr-1">{currentBadge.model}</span>
              · {currentBadge.provider}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Provider</label>
              <Select value={providerUI} onValueChange={handleProviderChange} disabled={saving}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {providerOptions.length === 0 && (
                    <SelectItem value={providerUI} className="text-xs">{providerUI}</SelectItem>
                  )}
                  {providerOptions.map(p => (
                    <SelectItem key={p.value} value={p.value} className="text-xs">{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                Modello
                {modelsLoading && <Loader2 className="h-3 w-3 inline ml-2 animate-spin" />}
              </label>
              <Select value={modelName} onValueChange={setModelName} disabled={saving || modelsLoading}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder={modelName || 'Seleziona modello'} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {availableModels.length === 0 && modelName && (
                    <SelectItem value={modelName} className="text-xs">{modelName}</SelectItem>
                  )}
                  {availableModels.map(m => (
                    <SelectItem key={m.id} value={m.id} className="text-xs">{m.name || m.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                Temperature ({temperature.toFixed(2)})
              </label>
              <Input
                type="number"
                min={0}
                max={1.5}
                step={0.1}
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                disabled={saving}
                className="h-9 text-xs"
              />
              <p className="text-[10px] text-muted-foreground mt-1">0 = deterministico · 1.5 = molto creativo (default 0.7)</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Max Tokens</label>
              <Input
                type="number"
                min={1}
                max={32000}
                step={100}
                value={maxTokens}
                onChange={(e) => setMaxTokens(parseInt(e.target.value, 10))}
                disabled={saving}
                className="h-9 text-xs"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Limite token risposta (default 2000, max 32000)</p>
            </div>
          </div>
        )}
        <div className="flex justify-end pt-2 border-t">
          <Button onClick={save} disabled={saving || loading} size="sm">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Salva configurazione
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
